import { brand } from "../../../../../../config/theme";
import { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useProvidersStore } from "../../../../../../store/reseller-admin/providersStore";
import type { Provider } from "../../../../../../store/reseller-admin/providersStore";
import { useProvidersList } from "../../../../../../features/reseller/queries";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import type { AlertState } from "../types";

export interface CredentialFieldForm {
  field: string;
  label: string;
  secret: boolean;
}

export interface EndpointPair {
  key: string;
  value: string;
}

export interface ProviderFormData {
  name: string;
  active: boolean;
  adapter: string;
  auth_strategy: string;
  default_discovery_interval_sec: string;
  default_poll_interval_sec: string;
  supports_discovery: boolean;
  endpoints_base: string;
  endpoints_extra: EndpointPair[];
  credentials_schema: CredentialFieldForm[];
}

const defaultFormValues: ProviderFormData = {
  name: "",
  active: true,
  adapter: "",
  auth_strategy: "",
  default_discovery_interval_sec: "3600",
  default_poll_interval_sec: "30",
  supports_discovery: false,
  endpoints_base: "",
  endpoints_extra: [],
  credentials_schema: [],
};

interface FilterParams {
  page: number;
  limit: number;
  name?: string;
  active?: string;
}

const providerToForm = (provider: Provider): ProviderFormData => {
  const endpoints = provider.endpoints || {};
  const { base, ...extra } = endpoints;
  return {
    name: provider.name || "",
    active: provider.active ?? true,
    adapter: provider.adapter || "",
    auth_strategy: provider.auth_strategy || "",
    default_discovery_interval_sec: provider.default_discovery_interval_sec || "",
    default_poll_interval_sec: provider.default_poll_interval_sec || "",
    supports_discovery: provider.supports_discovery ?? false,
    endpoints_base: base || "",
    endpoints_extra: Object.entries(extra).map(([key, value]) => ({
      key,
      value: String(value ?? ""),
    })),
    credentials_schema: (provider.credentials_schema || []).map((c) => ({
      field: c.field || "",
      label: c.label || "",
      secret: c.secret === true,
    })),
  };
};

const formToPayload = (data: ProviderFormData) => {
  const endpoints: Record<string, string> = {};
  if (data.endpoints_base.trim()) endpoints.base = data.endpoints_base.trim();
  (data.endpoints_extra || []).forEach((e) => {
    if (e.key.trim()) endpoints[e.key.trim()] = e.value;
  });
  return {
    name: data.name,
    active: data.active,
    adapter: data.adapter || null,
    auth_strategy: data.auth_strategy || null,
    default_discovery_interval_sec: data.default_discovery_interval_sec || null,
    default_poll_interval_sec: data.default_poll_interval_sec || null,
    supports_discovery: data.supports_discovery,
    endpoints,
    credentials_schema: (data.credentials_schema || []).filter(
      (c) => c.field.trim() !== "" || c.label.trim() !== ""
    ),
  };
};

export function useProviderForm() {
  const { t } = useTranslation("resellerAdmin");

  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "edit" | "add">("details");
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  const [showFormModal, setShowFormModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editProviderId, setEditProviderId] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<ProviderFormData | null>(null);
  // Id del proveedor cuyo detalle se está cargando para editar (loader en la fila/tarjeta).
  const [editingId, setEditingId] = useState<string | null>(null);

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const itemsPerPage = 8;
  const debouncedSearch = useDebounce(searchQuery);

  const {
    fetchProvider,
    createProvider,
    updateProvider,
    deleteProvider,
    fetchProvidersCount,
  } = useProvidersStore();

  const form = useForm<ProviderFormData>({ defaultValues: defaultFormValues });

  const filterParams = useMemo(
    (): FilterParams => ({
      page: currentPage,
      limit: itemsPerPage,
      name: debouncedSearch || undefined,
      active: selectedStatus !== "all" ? selectedStatus : undefined,
    }),
    [currentPage, itemsPerPage, selectedStatus, debouncedSearch],
  );

  const providersQuery = useProvidersList(filterParams);
  const providers = providersQuery.data?.data ?? [];
  const meta = providersQuery.data?.meta;
  const loading = providersQuery.isLoading;

  const queryClient = useQueryClient();
  const invalidateProviders = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["resellerProviders"] });
    queryClient.invalidateQueries({ queryKey: ["resellerCounts"] });
    fetchProvidersCount();
  }, [queryClient, fetchProvidersCount]);

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleDetailsClick = (provider: Provider) => {
    setSelectedProvider(provider);
    setModalType("details");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setEditProviderId(null);
    setOriginalData(null);
    form.reset(defaultFormValues);
    setModalType("add");
    setShowFormModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleEditClick = async (provider: Provider) => {
    setEditingId(provider.id);
    try {
      const full = await fetchProvider(provider.id);
      const providerData = (full as { data?: Provider })?.data ?? full ?? provider;
      const resetData = providerToForm(providerData);
      form.reset(resetData);
      setOriginalData(resetData);
      setEditProviderId(provider.id);
      setIsEdit(true);
      setModalType("edit");
      setShowFormModal(true);
      document.body.style.overflow = "hidden";
    } catch (error) {
      console.error("Error al cargar proveedor:", error);
      await Swal.fire({
        title: t("providers.alerts.error"),
        text: t("providers.alerts.loadError"),
        icon: "error",
        confirmButtonText: t("providers.actions.accept"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setEditingId(null);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProvider(null);
    document.body.style.overflow = "";
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setIsEdit(false);
    setEditProviderId(null);
    setOriginalData(null);
    setLoadingSubmit(false);
    form.reset(defaultFormValues);
    document.body.style.overflow = "";
  };

  const closeAlert = () => setAlert((prev) => ({ ...prev, show: false }));

  const hasFormChanges = () => {
    if (!isEdit || !originalData) return false;
    return JSON.stringify(form.getValues()) !== JSON.stringify(originalData);
  };

  const handleDeleteProvider = async (provider: Provider) => {
    const result = await Swal.fire({
      title: t("providers.alerts.confirmDeleteTitle"),
      text: t("providers.alerts.confirmDeleteText", { name: provider.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("providers.alerts.confirmDeleteBtn"),
      cancelButtonText: t("providers.alerts.cancelBtn"),
    });
    if (!result.isConfirmed) return;

    try {
      await deleteProvider(provider.id);
      await Swal.fire({
        title: t("providers.alerts.success"),
        text: t("providers.alerts.deleteSuccess"),
        icon: "success",
        confirmButtonText: t("providers.actions.accept"),
        confirmButtonColor: brand.primary,
      });
      invalidateProviders();
      closeModal();
      closeFormModal();
    } catch (error) {
      console.error("Error al eliminar proveedor:", error);
      await Swal.fire({
        title: t("providers.alerts.error"),
        text: t("providers.alerts.deleteError"),
        icon: "error",
        confirmButtonText: t("providers.actions.accept"),
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleSubmitProvider = async (data: ProviderFormData) => {
    setLoadingSubmit(true);
    try {
      const payload = formToPayload(data);

      if (isEdit && editProviderId) {
        await updateProvider(editProviderId, payload);
        await Swal.fire({
          title: t("providers.alerts.success"),
          text: t("providers.alerts.updateSuccess"),
          icon: "success",
          confirmButtonText: t("providers.actions.accept"),
          confirmButtonColor: brand.primary,
        });
      } else {
        await createProvider(payload);
        await Swal.fire({
          title: t("providers.alerts.success"),
          text: t("providers.alerts.createSuccess"),
          icon: "success",
          confirmButtonText: t("providers.actions.accept"),
          confirmButtonColor: brand.primary,
        });
      }

      closeFormModal();
      invalidateProviders();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      console.error("Error al guardar proveedor:", error);
      await Swal.fire({
        title: t("providers.alerts.error"),
        text:
          err?.response?.data?.message ||
          err?.message ||
          (isEdit
            ? t("providers.alerts.updateError")
            : t("providers.alerts.createError")),
        icon: "error",
        confirmButtonText: t("providers.actions.accept"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return {
    providers,
    meta,
    loading,
    selectedProvider,
    alert,
    isModalOpen,
    modalType,
    showFormModal,
    loadingSubmit,
    isEdit,
    editProviderId,
    editingId,
    form,
    selectedStatus,
    searchQuery,
    currentPage,
    selectedView,
    itemsPerPage,
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleAddClick,
    handleEditClick,
    handleSubmitProvider,
    handleDeleteProvider,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeFormModal,
    closeAlert,
    hasFormChanges,
  };
}
